import { faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'components/button/Button';
import { TextInput, TextInputProps } from 'components/forms/TextInput';
import { forwardRef, Ref, useState } from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

export type PasswordInputProps = Omit<TextInputProps, 'type'> & {
    /**
     * Controls the default visibility of the password field. Defaults to `false`.
     */
    defaultVisible?: boolean;
};

const VisibilityButton = styled(Button)`
    padding: 0;
`;

const Wrapper = styled.span`
    display: flex;
    flex-flow: row nowrap;
    gap: 0.5rem;
    position: relative;
    align-items: center;
`;

const Input = styled(TextInput).attrs({
    appearance: 'rounded',
})`
    background-color: ${tokens.grey.light};
    border: 1px solid ${tokens.nav.border};
    margin-top: 0.5rem;
    font-size: 1.4rem;

    flex-grow: 1;

    /* Edge/Trident: Hide browser show/hide password icon button */
    ::-ms-reveal {
        display: none;
    }
`;

const PasswordInput = (
    {
        onChange,
        required,
        value,
        className,
        placeholder,
        name,
        invalid,
        disabled,
        autofocus,
        showValidState = false,
        defaultVisible = false,
    }: PasswordInputProps,
    ref: Ref<HTMLInputElement>,
) => {
    const [visible, setVisible] = useState(defaultVisible);

    return (
        <Wrapper>
            <Input
                ref={ref}
                type={visible ? 'text' : 'password'}
                onChange={onChange}
                required={required}
                value={value}
                className={className}
                placeholder={placeholder}
                name={name}
                invalid={invalid}
                disabled={disabled}
                autofocus={autofocus}
                showValidState={showValidState}
            />
            <VisibilityButton
                type="button"
                onClick={() => setVisible(!visible)}
                aria-label="Toggle Visibility"
            >
                <FontAwesomeIcon icon={visible ? faEye : faEyeSlash} />
            </VisibilityButton>
        </Wrapper>
    );
};

const forwardRefPasswordInput = forwardRef(PasswordInput);

export { forwardRefPasswordInput as PasswordInput };
